import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHeader } from "../contexts/HeaderContext";
import { LanguageDropdown } from "./LanguageDropdown";
import { NotFoundCard } from "./NotFoundCard";
import { PayFooter } from "../pages/pay/PayFooter";
import LYRA_LOGO_FALLBACK from "../assets/lyra-mark.png";
import { resolveEntry } from "../utils/language";

// CXA-703: rendered by ConfigurationProvider when the site has flipped the
// WBE off. Deliberately does NOT mount the storefront's Header — the issue
// says only the logo + language switcher should show, no cart, no search.
// Reusing NotFoundCard keeps the "same size / same style as the 404 page"
// requirement without duplicating card chrome. Footer is the standard
// PayFooter so the site's contact info / policies still show under the card.
export const DisabledScreen = () => {
  const { t } = useTranslation();
  const { selectedLanguage, setSelectedLanguage, wbeConfig } = useHeader();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  // The admin's override for the language the guest is reading, if there is
  // one. Empty falls through to the built-in copy below, which lives in the
  // locale bundles because it is our text rather than the customer's — so it
  // exists in every language the engine serves without the API carrying it.
  const entry = resolveEntry(
    wbeConfig?.disabledMode?.entries,
    selectedLanguage,
  );
  const title = entry?.title.trim();
  const description = entry?.description.trim();
  const logoUrl = wbeConfig?.logotype?.url;

  return (
    <div
      data-testid="wbe-disabled-screen"
      className="flex min-h-screen flex-col bg-gradient-to-br from-surface-page to-surface-hover"
    >
      <header className="sticky top-0 z-40 border-b border-border bg-surface-page py-3 lg:py-6">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mx-auto flex max-w-[1024px] items-center justify-between">
            <img
              src={logoUrl ?? LYRA_LOGO_FALLBACK}
              alt={wbeConfig?.logotype?.description ?? "Site logotype icon"}
              className="h-10 w-14 object-contain"
            />
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-1 transition-opacity duration-200 hover:opacity-80"
                onClick={() => setIsLanguageDropdownOpen((open) => !open)}
              >
                <span className="text-xs font-medium text-ink/60">
                  {t(`language.${selectedLanguage}`)}
                </span>
              </button>
              <LanguageDropdown
                isOpen={isLanguageDropdownOpen}
                onClose={() => setIsLanguageDropdownOpen(false)}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <NotFoundCard
          // `||`, not `??`: a cleared override is an empty string, and that has
          // to fall through to the default the same way a missing one does.
          title={title || t("wbe_disabled.default_title")}
          description={description || t("wbe_disabled.default_description")}
          showStatusLabel={false}
          showWhatNowCallout={false}
        />
      </div>

      <PayFooter />
    </div>
  );
};
